import * as React from 'react';
import {
  DataGridPro,
  type GridColDef,
  type DataGridProProps,
  useGridApiContext,
  gridFilteredRowCountSelector,
  type GridValidRowModel,
} from '@mui/x-data-grid-pro';
import clsx from 'clsx';
import './ArrDataGridPro.scss';
import {ReactComponent as SortIcon} from '../../../assets/images/sort.svg';
import {ReactComponent as DescendingIcon} from '../../../assets/images/arrow-decrease.svg';
import {ReactComponent as AscendingIcon} from '../../../assets/images/arrow-increase.svg';
import {ReactComponent as FolderIcon} from '../../../assets/images/no-data-found.svg';
import {ReactElement, useMemo, useState} from 'react';
import ArrPagination from '../arr-pagination/ArrPagination';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrLoadingSpinner from '../arr-loading-spinner/ArrLoadingSpinner';
import ArrRadio from '../arr-radio/ArrRadio';
import ArrModal from '../arr-modal/ArrModal';
import {DataGridRowSelectionModel} from '../../../shared/interfaces';
import ArrCheckbox from '../arr-checkbox/ArrCheckbox';
import {useLocation} from 'react-router-dom';

export interface SelectionControlProps {
  selectedIds?: string[];
  unselectedIds?: string[];
  isAllSelected?: boolean;
}

export interface ArrDataGridProProps<
  R extends GridValidRowModel = GridValidRowModel,
>
  extends
    Omit<DataGridProProps, 'rows' | 'onRowOrderChange'>,
    SelectionControlProps {
  rows?: readonly R[];
  columns: GridColDef[];
  pageSize?: number;
  className?: string;
  currentPage?: number;
  setCurrentPage?: (arg0: number) => void;
  itemsPerPage?: number;
  setItemsPerPage?: (arg0: number) => void;
  onPageChange?: (pagenumber: number, pageSize?: number) => void;
  footerChildComponent?: ReactElement;
  entityType?: string;
  noRowsOverlay?: () => React.ReactElement | null;
  loadingOverlay?: () => React.ReactElement;
  primaryKey?: string;
  loading?: boolean;
  showPerPageOptions?: boolean;
  enableCheckbox?: boolean;
  selectedIds?: string[];
  unselectedIds?: string[];
  isAllSelected?: boolean;
  noRowsOverlayCustomText?: string;
  disableColumnResize?: boolean;
  autosizeOnMount?: boolean;
  onSelectionStateChange?: (
    rowSelectionModal: DataGridRowSelectionModel
  ) => void;
  gridHeight?: number;
  alwaysShowPagination?: boolean;
  showPanelToggle?: boolean;
  hideFilterIcon?: boolean;
  onRowsReorder?: (rows: R[]) => void;
}

const ArrDataGridPro = <R extends GridValidRowModel = GridValidRowModel>({
  rows,
  columns,
  className,
  currentPage = 1,
  setCurrentPage,
  itemsPerPage = 10,
  setItemsPerPage,
  onPageChange,
  footerChildComponent,
  entityType = '',
  primaryKey = 'id',
  rowCount = 0,
  noRowsOverlay,
  loadingOverlay,
  loading,
  showPerPageOptions,
  pageSizeOptions = [],
  enableCheckbox = false,
  selectedIds,
  unselectedIds,
  isAllSelected,
  noRowsOverlayCustomText,
  disableColumnResize = true,
  autosizeOnMount = false,
  onSelectionStateChange,
  gridHeight,
  alwaysShowPagination = false,
  showPanelToggle = false,
  hideFilterIcon = false,
  onRowsReorder,
  ...props
}: ArrDataGridProProps<R>) => {
  const totalItems = rowCount ? rowCount : (rows?.length ?? 0);
  const location = useLocation();

  const handleRowOrderChange: DataGridProProps['onRowOrderChange'] = (
    params
  ) => {
    if (onRowsReorder && rows) {
      const reorderedRows = [...rows];
      const [movedRow] = reorderedRows.splice(params.oldIndex, 1);
      reorderedRows.splice(params.targetIndex, 0, movedRow);
      onRowsReorder(reorderedRows);
    }
  };

  const [showModal, setShowModal] = useState<boolean>(false);
  const [radioValue, setRadioValue] = useState('false');
  const [modalDescription, setModalDescription] = useState<string>();
  const [isSelectionModal, setIsSelectionModal] = useState<boolean>(false);
  const [isSearchTermReport, setIsSearchTermReport] = useState<boolean>(false);
  const EmptyFilteredIcon = () => null;

  const pathname = location.pathname.toLowerCase();
  React.useEffect(() => {
    if (pathname?.includes('searchtermreport')) {
      setIsSearchTermReport(true);
    } else {
      setIsSearchTermReport(false);
    }
  }, []);

  const CustomSortIcon = () => {
    return (
      <SortIcon className="arr-datagrid__sort-icon" data-testid="sort-icon" />
    );
  };
  const CustomSortAscIcon = () => (
    <AscendingIcon
      className="arr-datagrid__sort-icon"
      data-testid="sort-asc-icon"
    />
  );
  const CustomSortDescIcon = () => (
    <DescendingIcon
      className="arr-datagrid__sort-icon"
      data-testid="sort-des-icon"
    />
  );
  const perPageOptions = useMemo(() => {
    const options = (pageSizeOptions as number[]).filter(
      (option) => option <= (totalItems || 0)
    );
    const additionalOption = (pageSizeOptions as number[]).find(
      (option) => option >= (totalItems || 0)
    );
    if (additionalOption && !options.includes(additionalOption)) {
      options.push(additionalOption);
    }
    return options;
  }, [pageSizeOptions, totalItems]);
  const CustomPagination: React.FC = () => {
    const apiRef = useGridApiContext();
    const handlePageChange = (page: number) => {
      apiRef.current.setPage(page - 1);
      setCurrentPage?.(page);
      onPageChange?.(page);
    };

    const setItemsPerPageHandler = (itemsPerPage: number) => {
      setItemsPerPage?.(itemsPerPage);
      apiRef.current.setPage(0);
      setCurrentPage?.(1);
      onPageChange?.(1, itemsPerPage);
      apiRef.current.setPageSize(itemsPerPage);
    };

    // When pagination mode is client, we need to use the filtered row count from the grid state instead of the total row count prop to get the correct number of pages in case of filtering, As parent won't be aware of internal filtering.
    const rowCountToUse =
      props.paginationMode === 'client'
        ? gridFilteredRowCountSelector(apiRef)
        : rowCount;

    return rowCountToUse > 0 ? (
      <div
        className="aur-datagrid-footer-child-container"
        data-testid={`arr-data-grid-prowrapper`}
      >
        {footerChildComponent ? footerChildComponent : null}
        <ArrPagination
          totalItems={rowCountToUse}
          perPage={itemsPerPage}
          currentPage={currentPage}
          entityType={entityType}
          onPerPageChange={setItemsPerPageHandler}
          onPageChange={handlePageChange}
          showPerPageOptions={showPerPageOptions}
          perPageOptions={perPageOptions}
          alwaysShow={alwaysShowPagination}
        />
      </div>
    ) : (
      <div
        className="aur-datagrid-footer-child-container"
        data-testid={`arr-data-grid-pro-empty-grid`}
      >
        {footerChildComponent ? footerChildComponent : null}
      </div>
    );
  };

  const CustomLoadingOverlay: React.FC = () => {
    return (
      <Box className="loading-overlay">
        <ArrLoadingSpinner show={loading} />
      </Box>
    );
  };

  const CustomNoRowsOverlay: React.FC = () => {
    return (
      <Box className="no-grid-overlay">
        <FolderIcon />
        <Typography
          className="typo-text headline2_size"
          data-testid={`arr-data-grid-pro-no-rows`}
        >
          {noRowsOverlayCustomText ??
            t('common.arrDataGrid.noResults', {entity: entityType})}
        </Typography>
      </Box>
    );
  };

  const handleStateChange = (data: DataGridRowSelectionModel) => {
    if (onSelectionStateChange)
      onSelectionStateChange({...data, totalCount: totalItems});
  };

  // #region checkbox functionality
  const clearState = () => {
    handleStateChange({
      isAllSelected: false,
      selectedIds: [],
      unselectedIds: [],
    });

    setRadioValue('false');
  };

  const selectAllCurrentPage = (checked: boolean) => {
    if (checked) {
      handleStateChange({
        isAllSelected: true,
        selectedIds: [],
        unselectedIds: [],
      });
    } else {
      clearState();
    }
  };

  const openSelectionModal = (isSelecting: boolean) => {
    setModalDescription(
      isSelecting
        ? t('common.arrDataGridPro.modal.selectAllPage')
        : t('common.arrDataGridPro.modal.unSelectAllPage')
    );
    setIsSelectionModal(isSelecting);
    setShowModal(true);
  };

  const handleAllSelect = (checked: boolean) => {
    setRadioValue('false');
    const isSmallPage = totalItems <= itemsPerPage;
    const hasUnselected = unselectedIds && unselectedIds.length > 0;
    const hasSelected = !isAllSelected && selectedIds && selectedIds.length > 0;

    if (isSmallPage) {
      selectAllCurrentPage(checked);
    } else {
      openSelectionModal(Boolean(checked || hasUnselected || hasSelected));
    }
  };

  const handleModalApply = () => {
    setShowModal(false);
    if (isSelectionModal) {
      if (radioValue === 'true') {
        handleStateChange({
          isAllSelected: true,
          selectedIds: [],
          unselectedIds: [],
        });
      } else {
        const rowIds = rows?.map((row) => row[primaryKey]) ?? [];
        handleStateChange({
          isAllSelected: false,
          selectedIds: selectedIds
            ? Array.from(new Set([...selectedIds, ...rowIds]))
            : rowIds,
          unselectedIds: [],
        });
      }
    } else {
      if (radioValue === 'true') {
        clearState();
      } else {
        const rowIds = rows?.map((row) => row[primaryKey]) ?? [];
        handleStateChange({
          isAllSelected: Boolean(isAllSelected),
          selectedIds: [],
          unselectedIds: unselectedIds
            ? Array.from(new Set([...unselectedIds, ...rowIds]))
            : rowIds,
        });
      }
    }
  };

  const handleSingleSelect = (id: string) => {
    if (isAllSelected) {
      if (!unselectedIds) return;
      const newUnselected = unselectedIds.includes(id)
        ? unselectedIds.filter((uid: string) => uid !== id)
        : [...unselectedIds, id];
      const allDeselected = newUnselected.length >= totalItems;
      handleStateChange({
        isAllSelected: !allDeselected,
        selectedIds: [],
        unselectedIds: allDeselected ? [] : newUnselected,
      });
    } else {
      if (!selectedIds) return;
      const newSelected = selectedIds.includes(id)
        ? selectedIds.filter((sid: string) => sid !== id)
        : [...selectedIds, id];
      //setSelectedIds(newSelected);
      handleStateChange({
        isAllSelected: false,
        selectedIds: newSelected,
        unselectedIds: unselectedIds ?? [],
      });
    }
  };
  //#endregion

  //#region DataGrid Columns
  const checkboxColumn: GridColDef = {
    field: '__checkbox__',
    width: 56,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    disableReorder: true,
    headerClassName: 'arr-checkbox-column-header',
    cellClassName: 'arr-checkbox-column-cell',
    renderHeader: () => (
      <Box tabIndex={0} data-testid="checkbox-column-box">
        <ArrCheckbox
          checked={Boolean(
            (isAllSelected &&
              unselectedIds &&
              unselectedIds.length != totalItems) ||
            (selectedIds &&
              selectedIds.length > 0 &&
              selectedIds.length <= totalItems)
          )}
          isIndeterminate={Boolean(
            (isAllSelected &&
              unselectedIds &&
              unselectedIds.length > 0 &&
              unselectedIds.length < totalItems) ||
            (!isAllSelected &&
              selectedIds &&
              selectedIds.length > 0 &&
              selectedIds.length < totalItems)
          )}
          onChange={(e) => {
            handleAllSelect(e);
          }}
          aria-label={t('common.arrDataGridPro.checkbox.selectAll')}
          size="medium"
          borderRadius={2}
          tabIndex={-1}
        />
      </Box>
    ),
    renderCell: (params) => (
      <ArrCheckbox
        checked={Boolean(
          (isAllSelected &&
            unselectedIds &&
            !unselectedIds.includes(params.row[primaryKey])) ||
          (selectedIds && selectedIds.includes(params.row[primaryKey]))
        )}
        onChange={() => handleSingleSelect(params.row[primaryKey])}
        size="medium"
        aria-label={`${t('common.arrDataGridPro.checkbox.selectRow')}  ${params.row[primaryKey]}`}
        borderRadius={2}
      />
    ),
  };

  const renderHeader = (name: string) => {
    return (
      <div data-testid={`header-div-${name}`}>
        <Typography
          tabIndex={0}
          className="typo-text label2_size"
          data-testid={`header-${name}`}
        >
          {name}
        </Typography>
      </div>
    );
  };

  const dataGridColumns: GridColDef[] = enableCheckbox
    ? [
        checkboxColumn,
        ...columns.map((column) => ({
          ...column,
          headerClassName: clsx(
            'typo-text label2_size',
            column.headerClassName
          ),
          cellClassName: clsx('typo-text body3_size', column.cellClassName),
        })),
      ]
    : columns.map((column) => {
        if (column.renderCell) {
          return {
            ...column,
            headerClassName: clsx(
              'typo-text label2_size',
              column.headerClassName
            ),
            cellClassName: clsx('typo-text body3_size', column.cellClassName),
            renderHeader:
              column.renderHeader ??
              (() => renderHeader(column.headerName ?? '')),
          };
        } else {
          return {
            ...column,
            headerClassName: clsx(
              'typo-text label2_size',
              column.headerClassName
            ),
            cellClassName: clsx('typo-text body3_size', column.cellClassName),
            renderCell: (params) => {
              const rowIndex = params.api.getRowIndexRelativeToVisibleRows(
                params.id
              );

              return (
                <div
                  data-testid={`cell-${column.headerName?.replaceAll(' ', '').toLowerCase()}-row-${rowIndex + 1}`}
                >
                  {params.value}
                </div>
              );
            },
            renderHeader:
              column.renderHeader ??
              (() => renderHeader(column.headerName ?? '')),
          };
        }
      });
  //#endregion DataGrid Columns

  return (
    <div
      className={clsx('arr-datagrid-pro', className)}
      style={{height: gridHeight}}
      data-testid={`arr-data-grid-pro-parent-div`}
    >
      <DataGridPro
        data-testid="arr-data-grid-pro"
        rows={rows}
        disableRowSelectionOnClick
        autosizeOnMount={autosizeOnMount}
        disableColumnMenu={true}
        disableColumnSelector={true}
        disableColumnResize={disableColumnResize}
        columns={dataGridColumns}
        localeText={{
          headerFilterOperatorContains: t(
            'common.arrDataGridPro.filterOperators.contains'
          ),
          headerFilterOperatorEquals: t(
            'common.arrDataGridPro.filterOperators.equals'
          ),
          headerFilterOperatorStartsWith: t(
            'common.arrDataGridPro.filterOperators.startsWith'
          ),
          headerFilterOperatorEndsWith: t(
            'common.arrDataGridPro.filterOperators.endsWith'
          ),
          headerFilterOperatorIs: t('common.arrDataGridPro.filterOperators.is'),
          headerFilterOperatorNot: t(
            'common.arrDataGridPro.filterOperators.not'
          ),
          headerFilterOperatorAfter: t(
            'common.arrDataGridPro.filterOperators.after'
          ),
          headerFilterOperatorOnOrAfter: t(
            'common.arrDataGridPro.filterOperators.onOrAfter'
          ),
          headerFilterOperatorBefore: t(
            'common.arrDataGridPro.filterOperators.before'
          ),
          headerFilterOperatorOnOrBefore: t(
            'common.arrDataGridPro.filterOperators.onOrBefore'
          ),
          headerFilterOperatorIsEmpty: t(
            'common.arrDataGridPro.filterOperators.isEmpty'
          ),
          headerFilterOperatorIsNotEmpty: t(
            'common.arrDataGridPro.filterOperators.isNotEmpty'
          ),
          headerFilterOperatorIsAnyOf: t(
            'common.arrDataGridPro.filterOperators.isAnyOf'
          ),
          ['headerFilterOperatorNotEquals' as any]: t(
            'common.arrDataGridPro.filterOperators.notEquals'
          ),
        }}
        slots={{
          columnUnsortedIcon: CustomSortIcon,
          columnSortedAscendingIcon: CustomSortAscIcon,
          columnSortedDescendingIcon: CustomSortDescIcon,
          ...(hideFilterIcon && {columnFilteredIcon: EmptyFilteredIcon}),
          noRowsOverlay: noRowsOverlay ?? CustomNoRowsOverlay,
          // Below prop is used when pagination mode is client and there are no results to show on the page.
          noResultsOverlay: noRowsOverlay ?? CustomNoRowsOverlay,
          pagination: CustomPagination,
          loadingOverlay: loadingOverlay ?? CustomLoadingOverlay,
          headerFilterMenu: null,
        }}
        getRowId={(row) => row[primaryKey]}
        getRowHeight={isSearchTermReport ? () => 'auto' : undefined}
        loading={loading}
        rowCount={rowCount}
        columnVisibilityModel={{
          __detail_panel_toggle__: showPanelToggle,
        }}
        onRowOrderChange={handleRowOrderChange}
        sx={
          isSearchTermReport && {
            '& .MuiDataGrid-cell': {
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: 1.4,
              alignItems: 'flex-start', // better for multi-line
              height: 'auto !important',
            },
          }
        }
        {...props}
      />
      {showModal && (
        <ArrModal
          showModal={showModal}
          onClose={() => setShowModal(false)}
          className="doc-list-modal"
          title={t('common.arrDataGridPro.modal.select')}
          description={modalDescription}
          dataTestId="arr-checkbox-modal"
          buttons={[
            {
              id: 'select-modal-cancel',
              label: t('common.arrDataGridPro.modal.button.cancel'),
              onClick: () => setShowModal(false),
              variant: 'secondary',
              dataTestId: 'arr-modal-cancel',
            },
            {
              id: 'select-modal-apply',
              label: t('common.arrDataGridPro.modal.button.apply'),
              onClick: handleModalApply,
              variant: 'primary',
              dataTestId: 'arr-modal-apply',
            },
          ]}
        >
          <Box className="doc-list-checkbox-modal">
            <ArrRadio
              variant="secondary"
              size="xs"
              className="doc-list-radio-btn"
              name="controlled-radio-buttons-group"
              onChange={(val) => setRadioValue(val)}
              value={radioValue}
              dataTestId="doc-list-radio-btn"
              data={[
                {
                  label: t('common.arrDataGridPro.modal.radioButton.yes'),
                  value: 'true',
                },
                {
                  label: t('common.arrDataGridPro.modal.radioButton.no'),
                  value: 'false',
                },
              ]}
            />
          </Box>
        </ArrModal>
      )}
    </div>
  );
};

export default ArrDataGridPro;
